/**
 * Thin fetch wrappers around the Auth Module's HTTP surface. These are NOT
 * TanStack Query hooks (Module 11) yet — that wiring lands when frontend-state
 * lands. Each function returns the parsed envelope so pages can render the
 * error code with the design.md tokens.
 *
 * Per agents/CONVENTIONS.md § Result-style returns: the server speaks the
 * `{ ok, data | error }` envelope; the client mirrors it here.
 */

export interface CurrentUserPayload {
  id: string;
  email: string;
  name: string;
  image_url: string | null;
  extended_time_multiplier: number;
  oauth_provider: 'google' | 'microsoft' | 'linkedin' | null;
  created_at: string;
}

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: { code: string; message: string; field?: string };
    };

async function postJson<T>(
  path: string,
  body: unknown,
): Promise<ApiEnvelope<T>> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: { code: 'internal', message: 'unexpected response shape' },
    };
  }
}

export interface SignUpInput {
  email: string;
  password: string;
  name: string;
  /** J-X13 — version strings of the ToS + Privacy Policy the user accepted. */
  tos_version?: string;
  privacy_version?: string;
}
export interface SignInInput {
  email: string;
  password: string;
}
export interface RequestResetInput {
  email: string;
}
export interface ResetInput {
  token: string;
  newPassword: string;
}

export const authApi = {
  signUp: (input: SignUpInput) =>
    postJson<{ user: CurrentUserPayload }>('/api/auth/sign-up', input),
  signIn: (input: SignInInput) =>
    postJson<{ user: CurrentUserPayload }>('/api/auth/sign-in', input),
  requestPasswordReset: (input: RequestResetInput) =>
    postJson<{ sent: true }>('/api/auth/request-password-reset', input),
  resetPassword: (input: ResetInput) =>
    postJson<null>('/api/auth/reset-password', input),
  signOut: () => postJson<null>('/api/auth/sign-out', {}),
  me: async (): Promise<ApiEnvelope<CurrentUserPayload | null>> => {
    let res: Response;
    try {
      res = await fetch('/api/me', { credentials: 'same-origin' });
    } catch {
      return { ok: false, error: { code: 'network', message: 'Network error — could not reach the server.' } };
    }
    const text = await res.text();
    try {
      return JSON.parse(text) as ApiEnvelope<CurrentUserPayload | null>;
    } catch {
      return { ok: false, error: { code: 'internal', message: 'unexpected response shape' } };
    }
  },
};
